interface FeedbackContext {
  isCorrect: boolean
  streak: number
  questionNumber: number
  totalQuestions: number
}

/**
 * Growth Mindset Motivational Messages (V6.0 - Simplified)
 *
 * RULES:
 * 1. NEVER use words: "Cito", "toets", "gokken", "fout"
 * 2. ALWAYS validate thinking before correcting
 * 3. Celebrate progress and effort
 */

export function getMotivationalMessage(context: FeedbackContext): string {
  const { isCorrect, streak } = context
  if (isCorrect) {
    // High streak celebration
    if (streak >= 5) {
      const messages = [
        'Niet te stoppen! 🔥',
        'Je bent on fire! 🚀',
        'Wat een reeks! Geweldig! ⭐',
        'Je gaat als een speer! ⚡',
        'Ongelofelijk goed bezig! 🏆',
      ]
      return messages[Math.floor(Math.random() * messages.length)]
    }

    // Standard correct messages
    const messages = [
      'Goed gedaan! 👍',
      'Helemaal goed! 🌟',
      'Lekker bezig! 💪',
      'Top! Je snapt het! ✨',
      'Precies goed! 🎯',
      'Netjes! Ga zo door! 🚀',
      'Uitstekend! 💎',
      'Klopt helemaal! ✅',
      'Super! Je groeit! 🌱',
      'Mooi werk! 🎨',
    ]
    return messages[Math.floor(Math.random() * messages.length)]
  } else {
    // Encouraging wrong answer messages
    const messages = [
      'Bijna! Laten we kijken waarom. 🔍',
      'Nog niet helemaal. Je leert hiervan! 📚',
      'Goede poging! Kijk naar de uitleg. 💡',
      'Dit is een leermoment! 🎓',
      'Niet erg, hiervan word je slimmer! 🧠',
      'Nog even oefenen. Je komt er wel! 👣',
      'Bijna goed! De uitleg helpt je. 📖',
      'Geef niet op! Probeer het te snappen. 💪',
      'Fouten maken mag! Zo leer je. 🌱',
      'Laten we het samen uitpluizen! 🤝',
    ]
    return messages[Math.floor(Math.random() * messages.length)]
  }
}

/**
 * Get level-up title based on level
 */
export function getLevelTitle(level: number): string {
  if (level === 1) return 'Ontdekker'
  if (level === 2) return 'Verkenner'
  if (level === 3) return 'Leerling'
  if (level === 4) return 'Oefenaar'
  if (level === 5) return 'Kenner'
  if (level === 6) return 'Slimmerik'
  if (level === 7) return 'Expert'
  if (level === 8) return 'Meester'
  if (level === 9) return 'Grootmeester'
  if (level === 10) return 'Kampioen'
  if (level === 11) return 'Held'
  if (level === 12) return 'Legende'
  if (level === 13) return 'Tovenaar'
  if (level === 14) return 'Fenomeen'
  if (level >= 15) return 'Ultieme Meester'
  return 'Ontdekker'
}

/**
 * Get level-up message
 */
export function getLevelUpMessage(level: number): string {
  const messages: Record<number, string> = {
    2: 'Je eerste stap! Ga zo door! 🌱',
    3: 'Wow! Je wordt steeds beter! 🌿',
    4: 'Lekker bezig! Je oefent goed! 🌿',
    5: 'Indrukwekkend! Je bent een echte kenner! 🌿',
    6: 'Wat ben jij slim! 🌳',
    7: 'Je bent een echte expert! 🌳',
    8: 'Meesterlijk! Je bent een meester! 🌳',
    9: 'Grootmeester! Wat een prestatie! 🌟',
    10: 'Kampioen! Je bent onverslaanbaar! 🌟',
    11: 'Een ware held! 🌟',
    12: 'Legendarisch! ⭐',
    13: 'Magisch! Je bent een tovenaar! ⭐',
    14: 'Fenomenaal! 👑',
    15: 'Ultieme Meester! Je hebt het hoogste level bereikt! 👑',
  }
  return messages[level] || 'Je bent weer een level omhoog! Top! 🎉'
}

/**
 * Get streak celebration message
 */
export function getStreakMessage(streak: number): string {
  if (streak === 5) {
    return '🔥 5 op rij! Je bent on fire!'
  } else if (streak === 10) {
    return '⚡ 10 op rij! Ongelofelijk!'
  } else if (streak === 25) {
    return '🌟 25 op rij! Fenomenaal!'
  } else if (streak === 50) {
    return '👑 50 op rij! Legendarisch!'
  } else if (streak % 10 === 0 && streak > 0) {
    return `🎯 ${streak} op rij! Blijf zo doorgaan!`
  }
  return ''
}
